import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { BrandMark } from '../components/BrandMark';
import { toast } from 'sonner';
import {
  Moon, Sun, Eye, EyeOff, CalendarCheck2, Activity, MessageCircle, Sparkles, ShieldCheck,
} from 'lucide-react';
import axios from 'axios';

import { API } from '@/lib/api';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

/** Lo que la clínica gana con Dentu: se muestra mientras espera el login. */
const BENEFICIOS = [
  { icon: CalendarCheck2, titulo: 'Agenda que se llena sola', texto: 'Citas, recordatorios y confirmaciones sin ir y venir por WhatsApp.' },
  { icon: Activity,       titulo: 'Seguimiento del paciente',  texto: 'Odontograma, notas clínicas y evolución en una sola ficha.' },
  { icon: MessageCircle,  titulo: 'Bot que atiende 24/7',      texto: 'Agenda, reagenda y responde dudas aunque la clínica esté cerrada.' },
];

export const LoginPage = () => {
  const [isLoading, setIsLoading]         = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword]   = useState(false);
  const [clinicName, setClinicName]       = useState('');
  const [clinicPhone, setClinicPhone]     = useState('');

  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');

  const { login, loginWithGoogle } = useAuth();
  const { theme, toggleTheme }     = useTheme();
  const navigate                   = useNavigate();
  const googleBtnRef               = useRef(null);
  const gisLoaded                  = useRef(false);

  useEffect(() => {
    axios.get(`${API}/config`)
      .then(res => {
        setClinicName(res.data.clinic_name || '');
        setClinicPhone(res.data.clinic_phone || '');
      })
      .catch(() => {});
  }, []);

  // Carga Google Identity Services y renderiza el botón oficial de Google
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || gisLoaded.current) return;

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = () => {
      gisLoaded.current = true;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCredential,
        ux_mode: 'popup',
      });
      if (googleBtnRef.current) {
        window.google.accounts.id.renderButton(googleBtnRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          locale: 'es',
          width: 320,
        });
      }
    };
    document.body.appendChild(script);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGoogleCredential = async (response) => {
    setGoogleLoading(true);
    try {
      const user = await loginWithGoogle(response.credential);
      toast.success(`Bienvenido, ${user.nombre}`);
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo iniciar sesión con Google');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Bienvenido, ${user.nombre}`);
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Credenciales inválidas');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-background lg:grid lg:grid-cols-[1.05fr_minmax(440px,0.95fr)]">
      {/* ── Panel de marca: explica el producto, no solo decora ─────────── */}
      <section className="relative hidden overflow-hidden bg-[hsl(213_45%_10%)] text-white lg:flex lg:flex-col lg:justify-between lg:p-12 xl:p-14">
        <div className="absolute inset-0 bg-mesh opacity-90" aria-hidden="true" />
        <div className="absolute inset-0 bg-dots opacity-[0.18]" aria-hidden="true" />
        <div
          className="absolute -right-24 -top-24 h-72 w-72 rounded-full blur-3xl"
          style={{ background: 'radial-gradient(circle, hsl(var(--brand-from) / 0.45), transparent 70%)' }}
          aria-hidden="true"
        />

        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-xl bg-white/10 backdrop-blur-sm ring-1 ring-white/15">
              <BrandMark className="h-6 w-6" />
            </div>
            <div className="leading-tight">
              <p className="font-display text-lg font-extrabold tracking-tight">Dentu</p>
              <p className="text-[11px] text-white/60">Gestión de clínica dental</p>
            </div>
          </div>

          <h1 className="mt-14 max-w-[24ch] font-display text-4xl font-extrabold leading-[1.08] tracking-tight xl:text-[42px]">
            El sistema que trabaja mientras la clínica atiende.
          </h1>
          <p className="mt-4 max-w-[42ch] text-[15px] leading-relaxed text-white/70">
            {clinicName ? `Espacio de trabajo de ${clinicName}.` : 'Tu consultorio, ordenado desde la primera cita del día.'}
          </p>

          <ul className="mt-10 space-y-5">
            {BENEFICIOS.map(({ icon: Icon, titulo, texto }) => (
              <li key={titulo} className="flex gap-3.5">
                <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-white/[0.08] ring-1 ring-white/10">
                  <Icon className="h-4.5 w-4.5 text-[hsl(var(--brand-from))]" />
                </span>
                <div>
                  <p className="text-sm font-semibold">{titulo}</p>
                  <p className="mt-0.5 max-w-[38ch] text-[13px] leading-relaxed text-white/60">{texto}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Mock del día: da textura de producto real */}
        <div className="relative mt-12 max-w-md rounded-xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur-sm">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/50">Hoy · agenda</p>
            <span className="flex items-center gap-1.5 text-[11px] text-white/60">
              <span className="h-1.5 w-1.5 rounded-full bg-[hsl(var(--brand-from))]" /> en vivo
            </span>
          </div>
          <div className="mt-3 space-y-2">
            {[
              { h: '09:00', n: 'Ana Paula Z.', m: 'Limpieza dental', c: 'hsl(var(--brand-from))' },
              { h: '10:30', n: 'Jorge R.', m: 'Ortodoncia · ajuste', c: 'hsl(272 58% 62%)' },
              { h: '12:00', n: 'Camila E.', m: 'Resina en molar', c: 'hsl(160 66% 48%)' },
            ].map((cita) => (
              <div key={cita.h} className="flex items-center gap-3 rounded-lg bg-white/[0.05] px-3 py-2">
                <span className="tnum text-xs font-semibold text-white/80">{cita.h}</span>
                <span className="h-4 w-[3px] rounded-full" style={{ background: cita.c }} />
                <span className="truncate text-sm font-medium">{cita.n}</span>
                <span className="ml-auto truncate text-xs text-white/55">{cita.m}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative mt-8 flex items-center gap-2 text-[12px] text-white/45">
          <ShieldCheck className="h-4 w-4" />
          Datos clínicos cifrados y respaldados a diario.
        </div>
      </section>

      {/* ── Formulario ──────────────────────────────────────────────────── */}
      <section className="relative flex min-h-screen flex-col items-center justify-center px-5 py-12 sm:px-10">
        <div className="absolute right-4 top-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            data-testid="theme-toggle-login"
            aria-label={theme === 'dark' ? 'Activar modo claro' : 'Activar modo oscuro'}
          >
            {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
        </div>

        <div className="w-full max-w-[380px] animate-fade-up">
          {/* Marca compacta: en móvil el panel lateral no se ve */}
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-brand-gradient">
              <BrandMark className="h-6 w-6" />
            </div>
            <div className="leading-tight">
              <p className="font-display text-base font-extrabold tracking-tight">Dentu</p>
              <p className="text-[11px] text-muted-foreground">{clinicName || 'Gestión clínica'}</p>
            </div>
          </div>

          <div className="mb-7">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/[0.08] px-2.5 py-1 text-[11px] font-semibold text-primary">
              <Sparkles className="h-3.5 w-3.5" /> Acceso del personal
            </span>
            <h2 className="mt-3 font-display text-[26px] font-extrabold leading-tight tracking-tight">
              {clinicName || 'Clínica Dental'}
            </h2>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Entra con tu cuenta para ver la agenda y los expedientes de hoy.
            </p>
          </div>

          {GOOGLE_CLIENT_ID && (
            <div className="mb-6 space-y-3">
              <div ref={googleBtnRef} className="flex justify-center" data-testid="google-signin-btn" />
              {googleLoading && (
                <p className="text-center text-sm text-muted-foreground">Verificando con Google…</p>
              )}
              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-border" />
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground">o con tu correo</span>
                <div className="h-px flex-1 bg-border" />
              </div>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-[13px] font-medium">Correo electrónico</Label>
              <Input
                id="email"
                type="email"
                placeholder="correo@clinica.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                data-testid="login-email"
                className="h-11"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-[13px] font-medium">Contraseña</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  data-testid="login-password"
                  className="h-11 pr-11"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3 text-muted-foreground hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <Button
              type="submit"
              className="h-11 w-full bg-brand-gradient text-white shadow-[var(--shadow-glow)] transition-transform active:scale-[0.985] hover:opacity-95"
              disabled={isLoading}
              data-testid="login-submit"
            >
              {isLoading ? 'Ingresando…' : 'Iniciar sesión'}
            </Button>
          </form>

          <div className="mt-8 border-t border-border/70 pt-4 text-[12px] leading-relaxed text-muted-foreground">
            ¿Problemas para entrar?
            {clinicPhone ? (
              <> Llama a recepción al <span className="font-medium text-foreground tnum">{clinicPhone}</span>.</>
            ) : (
              <> Pide a la administradora que resetee tu contraseña.</>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};
